import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { reviewQueueKey } from '@/entities/deck';
import { getReviewQueue } from '@/shared/api/review';
import { currentTimeZone } from '@/shared/lib/timeZone';
import { ErrorMessage } from '@/shared/ui';

import { ReviewCard } from './ReviewCard';
import styles from './ReviewSession.module.css';

export interface ReviewSessionProps {
  deckId?: string;
}

/**
 * Works through the queue one card at a time. When a batch runs out the queue
 * is fetched again, because cards answered a minute ago may already be due.
 */
export function ReviewSession({ deckId }: ReviewSessionProps): React.JSX.Element {
  const { t } = useTranslation();
  const queue = useQuery({
    queryKey: [...reviewQueueKey, deckId ?? 'all'],
    queryFn: ({ signal }) =>
      getReviewQueue({ timeZone: currentTimeZone(), signal, ...(deckId ? { deckId } : {}) }),
    staleTime: 0,
    gcTime: 0,
  });

  const [progress, setProgress] = useState({ batch: 0, index: 0 });
  const index = progress.batch === queue.dataUpdatedAt ? progress.index : 0;

  if (queue.isPending || queue.isFetching) {
    return <p className={styles.meta}>{t('review.loading')}</p>;
  }

  if (queue.isError) {
    return <ErrorMessage error={queue.error} />;
  }

  const items = queue.data.items;
  const item = items[index];

  if (item === undefined) {
    return (
      <section className={styles.done}>
        <h2>{t('review.doneTitle')}</h2>
        <p className={styles.meta}>{t('review.doneText')}</p>
      </section>
    );
  }

  const onAnswered = (): void => {
    if (index + 1 < items.length) {
      setProgress({ batch: queue.dataUpdatedAt, index: index + 1 });

      return;
    }

    void queue.refetch();
  };

  return (
    <section className={styles.session}>
      <p className={styles.meta}>{t('review.remaining', { count: items.length - index })}</p>
      <ReviewCard
        key={`${String(queue.dataUpdatedAt)}-${String(index)}`}
        item={item}
        onAnswered={onAnswered}
      />
    </section>
  );
}
